<template>
    <div class="lg:tw-relative">
        <AcquisitionFormProgress
            :total-steps="2"
            :step="1"
            :class="{ 'tw-text-left !tw-left-0 !tw-relative !tw-top-0': isAsiInquiry }"
        />
        <Heading v-if="!isAsiInquiry" :size="2" :title="headline" />
        <TrustpilotRating v-if="!isAsiInquiry">
            <p class="tw-font-medium">Kunden vertrauen auf uns.</p>
            <p>Unsere lokalen Experten begeistern mit maßgeschneiderten Traumreisen!</p>
        </TrustpilotRating>
        <info-snippet
            v-if="(country && !country.mainCountry.isTourOperatorGermanSpeaking) || tour && !tour.mainCountry.isTourOperatorGermanSpeaking"
            icon="languageIcon"
            hasBackground
            :class="{ '!tw-mt-20': isAsiInquiry }"
        >
            Unsere Experten {{ countryName ? 'in ' + countryName : '' }} sprechen zwar nur Englisch, kennen ihr Land aber umso besser.
        </info-snippet>
        <time-selector :country-name="country?.title || tour.mainCountry.staticCountry.shortName" />
        <duration-selector />
        <info-snippet v-if="!isAsiInquiry" icon="MapIcon" hasBackground>
            Deine Angaben helfen unseren lokalen Reiseexperten, Dir einen maßgeschneiderten Reisevorschlag zu erstellen.
        </info-snippet>
        <person-count :country-name="country?.title || tour.mainCountry.staticCountry.shortName" />
        <budget-selector />
        <tour-questions 
            v-if="tour?.mainCountry.tourQuestions && tour?.mainCountry?.inquiryType === 'questions-inquiry'" 
            :tour="tour" 
            @answers-updated="handleTourQuestionAnswers"
        />
        <travel-message-input :country-name="country?.title || tour.mainCountry.staticCountry.shortName" :country="country" :inquiryType="travelData.inquiryType"/>
        <custom-button
            @click="routeChange()"
            :disabled="!travelData.month || !travelData.year || !allTourQuestionsAnswered"
        >
            Weiter &rarr;
        </custom-button>
        <testimonials @mounted="initTrustpilot" v-if="!isAsiInquiry" />
    </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import { getMarketingContext } from "../utils/marketingContext.js";
import CustomButton from "../components/baseComponents/CustomButton.vue";
import DurationSelector from "../components/travelDataComponents/DurationSelector.vue";
import TimeSelector from "../components/travelDataComponents/TimeSelector.vue";
import PersonCount from "../components/travelDataComponents/PersonCount.vue";
import BudgetSelector from "../components/travelDataComponents/BudgetSelector.vue";
import TravelMessageInput from "../components/travelDataComponents/TravelMessageInput.vue";
import InfoSnippet from "../components/travelDataComponents/InfoSnippet.vue";
import Testimonials from "../components/travelDataComponents/Testimonials.vue";
import TrustpilotRating from '../components/travelDataComponents/TrustpilotRating.vue';
import AcquisitionFormProgress from "../components/baseComponents/AcquisitionFormProgress.vue";
import TourQuestions from "../components/travelDataComponents/TourQuestions.vue";

export default {
    name: 'TravelData',
    components: {
        AcquisitionFormProgress,
        Testimonials,
        TravelMessageInput,
        BudgetSelector,
        PersonCount,
        TimeSelector,
        DurationSelector,
        CustomButton,
        InfoSnippet,
        TrustpilotRating,
        TourQuestions
    },
    data: () => ({
        formStarted: false,
    }),
    created () {
        if(localStorage.getItem('travelData')) {
            const storedData = JSON.parse(localStorage.getItem('travelData'))
            // Only load the stored data if the current route matches either the
            // stored tour or country.
            if (
                this.tour && storedData.tour === this.tour.uid ||
                this.country && storedData.destinationCountry === this.country.uid
            ) {
                // The inquiryType is determined by the current page (data-inquiry-type
                // on the app root), not by previously stored data — otherwise an
                // earlier WDT inquiry would leak into the ASI flow and vice versa.
                storedData.inquiryType = this.travelData.inquiryType;
                this.$store.commit('tour/setTravelData', storedData);
            }
        }
    },
    mounted() {
        // Initialize TrustPilot widget
        this.initTrustpilot();

        if(!this.travelData.inquiryType) {
            this.$store.commit('tour/setInquiryType', 'inquiry-form-2.0');
        }

        // inquiryStart is currently locked on GTM side to only fire when triggered from /de/inq or /de/reise-planen
        this.$gtm.trackEvent({
            event: 'inquiryStart',
            travelType: window.location.pathname.includes('reise-planen') ? 'country' : 'tour',
            country: this.country?.mainCountry.staticCountry.shortName || this.tour.mainCountry.staticCountry.shortName,
            inquiryType: this.travelData.inquiryType,
            source: this.isAsiInquiry ? 'ASI-Tailor-Made' : 'WDT',
            ...getMarketingContext(),
        })
    },
    watch: {
        'travelData.year'() { this.trackFormStarted() },
        'travelData.month'() { this.trackFormStarted() },
    },
    computed: {
        ...mapGetters({
            travelData: 'tour/travelData',
            tour: 'tour/route',
            country: 'tour/country',
            inquiry: 'tour/inquiry',
            isAsiInquiry: 'tour/isAsiInquiry',
        }),
        isCountryAcquisition() {
            return document.URL.includes('/reise-planen/');
        },
        countryName() {
            return this.country?.title || this.tour.mainCountry.staticCountry.shortName;
        },
        headline() {
            let headline = ''
            if (this.country) {
                headline = `Plane deine Rundreise nach ${this.country.title} in nur 2 Minuten`
            } else if (this.tour) {
                headline = this.tour.title
            } else {
                headline = 'Plane deine Reise in nur 2 Minuten'
            }
            return headline
        },
        allTourQuestionsAnswered() {
            // First check if there are tour questions available from the tour data
            const availableQuestions = this.tour?.mainCountry?.tourQuestions;
            if (!availableQuestions || availableQuestions.length === 0 || this.travelData.inquiryType !== 'questions-inquiry') {
                return true;
            }
            
            // Check if all questions have at least one answer
            const tourQuestions = this.travelData.tourQuestions || [];

            // Questions are only being added to travelData upon interaction
            if (tourQuestions.length !== availableQuestions.length) {
                return false;
            }

            const result = tourQuestions.every(answerObj => {
                if (!answerObj) {
                    return false;
                }
                
                // Check if at least one answer is selected (array has items)
                return answerObj.answer.length > 0;
            });
            
            return result;
        }
    },
    methods: {
        ...mapActions({
            loadTour: 'tour/loadTour',
            loadCountry: 'tour/loadCountry',
        }),
        trackFormStarted() {
            if (this.formStarted) return;
            this.formStarted = true;
            this.$gtm.trackEvent({
                event: 'inquiryFormStart',
                inquiryType: this.travelData.inquiryType,
                source: this.isAsiInquiry ? 'ASI-Tailor-Made' : 'WDT',
                country: this.country?.mainCountry.staticCountry.shortName || this.tour?.mainCountry.staticCountry.shortName,
                ...getMarketingContext(),
            });
        },
        // Initialize TrustPilot widget with Firefox private browsing detection
        initTrustpilot() {
            try {
                if (window.Trustpilot) {
                    window.Trustpilot.loadFromElement(document.querySelector('.trustpilot-widget'));
                } else {
                    // Check if we might be in Firefox private browsing
                    const isPrivateMode = !window.indexedDB;
                    if (isPrivateMode) {
                        console.log('TrustPilot widget may be blocked by Firefox private browsing mode');
                    }
                }
            } catch (error) {
                console.log('Error initializing TrustPilot widget:', error);
            }
        },
        async routeChange() {
            if(this.tour) {
                await this.$store.commit('tour/setTour', this.tour.uid);
            } else {
                await this.$store.commit('tour/setDestinationCountry', this.country.uid);
            }
            localStorage.setItem('travelData', JSON.stringify(this.travelData));
            localStorage.setItem('inquiry', JSON.stringify(this.inquiry));
            this.$gtm.trackEvent({
                event: 'inquiryStepOne',
                year: this.travelData.year,
                month: this.travelData.fullMonthName,
                duration: this.travelData.duration,
                numPeople: this.travelData.groupSize.adults + this.travelData.groupSize.children + this.travelData.groupSize.infants,
                budget: this.travelData.budget,
                messageLength: this.travelData.message ? this.travelData.message.length : 0,
                country: this.country ? this.country.mainCountry.staticCountry.shortName : this.tour.mainCountry.staticCountry.shortName
            })
            this.$router.push({name: 'userData'});
        },
        handleTourQuestionAnswers(answers) {
            this.$store.commit('tour/setTravelData', { ...this.travelData, tourQuestions: answers });
        }
    }
}

</script>

<style scoped>

</style>